import React from 'react'
import PropTypes from 'prop-types'
import { useSelector } from 'react-redux'
import {
  Box, Typography, Button, Stack, Tooltip, emotionStyled as styled,
} from '@stvgroup/stv-ui-components'
import { Captions, Play as PlayIcon } from 'lucide-react'
import { colors } from '../../styles'
import { MyListButton } from '..'
import ForceAdsWarning from '../AdLabels/ForceAdsWarning'
import HeroLiveIcon from '../icons/HeroLiveIcon'
import SvodExclusiveWarning from '../AdLabels/SvodExclusiveWarning'
import PadlockIcon from '../icons/PadlockIcon'
import EllipseIcon from '../icons/EllipseIcon'
import {
  GuideIcon,
  AudioDescibedIcon,
  VisuallySignedIcon,
  InformationIcon,
} from '../icons'
import config from './config'
import { trackEvent } from '../../utils/dtmHelpers'
import { sendGoogleTagEvent } from '../../utils/google-tag-manager-helpers'
import getImageRenditions from '../../utils/getImageRenditions'

const WatchLiveButton = styled(Button)({
  backgroundColor: '#C12A26',
  color: colors.white,
  '&:hover': {
    backgroundColor: colors.hoverRed,
  },
  '&:visited': {
    color: 'inherit',
  },
})

const HeroCTA = React.forwardRef(({
  title,
  titleTextImage,
  text,
  buttonText,
  buttonLink,
  buttonType,
  forceAds,
  svodExclusive,
  genre,
  availableEpisodes,
  isGuidance,
  audioDescribed,
  visuallySigned,
  isSubtitle,
  programmeDetails,
  hasInformation,
  informationText,
  competitionText,
  hasCompetitionText,
  isStvLiveProg,
  index,
  liveProgramme,
  sx,
  showMyListButton,
  button,
}, ref) => {
  const users = useSelector(state => state.user)

  const getButtonIcon = () => {
    if (buttonType === 'watchNow') return <PlayIcon fill="black" />
    if (buttonType === 'watchLive' || liveProgramme) return <HeroLiveIcon />
    if (svodExclusive && !users.currentlySubscribed) return <PadlockIcon />
    return null
  }

  const capitalizeFirstLetter = (string) => {
    if (!string) return null
    let splitedString = string.split(/(?=[A-Z])/).join(' ')
    splitedString = splitedString.charAt(0).toUpperCase() + splitedString.slice(1)
    return splitedString
  }

  const getButtonText = () => {
    if (liveProgramme) return 'Watch Live'
    if (svodExclusive && !users.currentlySubscribed) return 'Upgrade to STV Player+'
    if (buttonType === 'custom' || buttonText) return buttonText
    return capitalizeFirstLetter(buttonType)
  }

  const getButtonLink = () => {
    if (liveProgramme) return '/live/stv'
    if (svodExclusive && !users.currentlySubscribed) return 'https://my.stv.tv/plan'
    return buttonLink
  }

  const analyticsTracking = (event) => {
    const tilePosition = index + 1
    trackEvent('homepageClick', {
      'stv.homepage.row': 'Hero',
      'stv.homepage.rownumber': 0,
      'stv.homepage.column': tilePosition,
      'stv.card.title': event.target.title,
    })
    sendGoogleTagEvent('Watch Now Button', {
      item: event.target.title,
    })
  }

  const hasIcons = isGuidance || audioDescribed || visuallySigned || isSubtitle
    || hasInformation || hasCompetitionText || svodExclusive || forceAds
  const showForceAdsIcon = forceAds && users.currentlySubscribed
  const showSvodExclusiveIcon = svodExclusive && !users.currentlySubscribed

  const titleImageRenditionUrl = titleTextImage?._filepath
    ? getImageRenditions(titleTextImage._filepath, ['472x172'], true)._renditions['472x172']
    : null

  return (
    <Stack spacing={{ xs: 2, md: 3 }} sx={sx} ref={ref}>
      <Box
        className="hero-programme-title"
        sx={{
          height: { xs: 124, md: 124, lg: 172 },
          display: 'flex',
          alignItems: 'flex-end',
        }}
      >
        {titleImageRenditionUrl ? (
          <Box
            component="img"
            src={titleImageRenditionUrl}
            alt={title}
            sx={{
              maxHeight: { xs: 124, md: 124, lg: 172 },
              maxWidth: { xs: 272, md: 272, lg: 472 },
              objectFit: 'contain',
              objectPosition: { xs: 'center', md: 'left' },
            }}
          />
        ) : (
          <Typography
            variant="h1"
            sx={{
              fontSize: { xs: '32px', md: '40px' },
              mb: 0,
              textShadow: '0px 1px 3px rgba(0, 0, 0, 0.15), 0px 1px 2px rgba(0, 0, 0, 0.3)',
              textAlign: { xs: 'center', md: 'left' },
            }}
          >{title}
          </Typography>
        )}
      </Box>
      <Stack spacing={{ xs: 1, md: 2 }} alignItems={{ xs: 'center', md: 'flex-start' }}>
        <Stack direction="row" spacing={1} alignItems="center" justifyContent="center" sx={{ flexWrap: 'wrap' }}>
          {genre && <Typography variant="body1">{genre}</Typography>}
          {genre && availableEpisodes && <EllipseIcon />}
          {availableEpisodes && <Typography variant="body1" sx={{ whiteSpace: 'nowrap' }}>{availableEpisodes}</Typography>}
          {availableEpisodes && hasIcons && <EllipseIcon />}
          {hasIcons && (
            <Stack direction="row" spacing={1}>
              {isSubtitle && <Tooltip title={config.subtitle}><Captions /></Tooltip>}
              {isGuidance && <Tooltip title={config.guidance}><GuideIcon /></Tooltip>}
              {audioDescribed && <Tooltip title={config.audioDesc}><AudioDescibedIcon /></Tooltip>}
              {visuallySigned && <Tooltip title={config.signed}><VisuallySignedIcon /></Tooltip>}
              {hasInformation && <Tooltip title={informationText}><InformationIcon /></Tooltip>}
              {hasCompetitionText && <Tooltip title={competitionText}><InformationIcon /></Tooltip>}
              {(showForceAdsIcon || showSvodExclusiveIcon) && <EllipseIcon />}
              {svodExclusive && !users.currentlySubscribed && (
                <Tooltip title={config.svodExculsive}>
                  <SvodExclusiveWarning />
                </Tooltip>
              )}
              {forceAds && <Tooltip title={config.forceAds}><ForceAdsWarning /></Tooltip>}
            </Stack>
          )}
        </Stack>

        <Typography sx={{
          fontWeight: 400,
          fontSize: '1.1em',
          lineHeight: '26px',
          m: 0,
          wordWrap: 'break-word',
          textAlign: { xs: 'center', md: 'left' },
          textShadow: '0px 1px 3px rgba(0, 0, 0, 0.15), 0px 1px 2px rgba(0, 0, 0, 0.3)',
        }}
        >
          {text}
        </Typography>

        {svodExclusive && <SvodExclusiveWarning includeText />}
        {forceAds && <ForceAdsWarning includeText />}
      </Stack>

      {buttonType !== 'none' && (
        <Stack direction={{ xs: 'column', md: 'row' }} alignItems="center" sx={{ width: { xs: '100%', sm: 'auto' } }}>
          {button || ((buttonType === 'watchLive' || liveProgramme) ? (
            <WatchLiveButton
              variant="contained"
              href={getButtonLink()}
              title={title ?? 'No Title'}
              onClick={analyticsTracking}
              startIcon={getButtonIcon()}
              sx={{
                width: { xs: '100%', md: 'auto' },
              }}
            >
              {getButtonText()}
            </WatchLiveButton>
          ) : (
            <Button
              variant="contained"
              href={getButtonLink()}
              title={title ?? 'No Title'}
              onClick={analyticsTracking}
              startIcon={getButtonIcon()}
              sx={{
                width: { xs: '100%', md: 'auto' },
                mb: 0,
              }}
            >
              {getButtonText()}
            </Button>
          ))}
          {showMyListButton && (
            <MyListButton
              programme={{
                name: programmeDetails.title,
                image: programmeDetails.images,
                programmeGuid: programmeDetails.guid,
              }}
              sx={{
                ml: { xs: 0, md: '50px' },
                mb: 0,
                justifyContent: { xs: 'center', md: 'flex-start' },
              }}
            />
          )}
        </Stack>
      )}

    </Stack>
  )
})

HeroCTA.propTypes = {
  title: PropTypes.string,
  titleTextImage: PropTypes.shape({
    _filepath: PropTypes.string,
    _renditions: PropTypes.object,
  }),
  text: PropTypes.string,
  buttonText: PropTypes.string,
  buttonLink: PropTypes.string,
  buttonType: PropTypes.string,
  forceAds: PropTypes.bool,
  svodExclusive: PropTypes.bool,
  genre: PropTypes.string,
  availableEpisodes: PropTypes.string,
  isGuidance: PropTypes.bool,
  audioDescribed: PropTypes.bool,
  visuallySigned: PropTypes.bool,
  isSubtitle: PropTypes.bool,
  programmeDetails: PropTypes.shape({
    title: PropTypes.string,
    images: PropTypes.string,
    guid: PropTypes.string,
  }),
  hasInformation: PropTypes.bool,
  informationText: PropTypes.string,
  competitionText: PropTypes.string,
  hasCompetitionText: PropTypes.bool,
  isStvLiveProg: PropTypes.bool,
  index: PropTypes.number,
  liveProgramme: PropTypes.bool,
  sx: PropTypes.shape({}),
  showMyListButton: PropTypes.bool,
  button: PropTypes.node,
}

HeroCTA.defaultProps = {
  title: '',
  titleTextImage: {
    _filepath: '',
  },
  text: '',
  buttonText: '',
  buttonLink: '',
  buttonType: 'watchNow',
  forceAds: false,
  svodExclusive: false,
  genre: '',
  availableEpisodes: '',
  isGuidance: false,
  audioDescribed: false,
  visuallySigned: false,
  isSubtitle: false,
  programmeDetails: {
    title: '',
    images: '',
    guid: '',
  },
  hasInformation: false,
  informationText: '',
  competitionText: '',
  hasCompetitionText: false,
  isStvLiveProg: false,
  index: 0,
  liveProgramme: false,
  sx: {},
  showMyListButton: false,
  button: null,
}

export default HeroCTA
